import React from 'react'
import styled, { css, keyframes } from 'styled-components'

export const spinnerKeyframes = keyframes`
  // Start in initial position with initial size:
  0% {
    transform: rotate(0deg) scale(1);
  }

  // When animation is halfway complete spinner should have completed a half rotation and have grown slightly:
  50% {
    transform: rotate(180deg) scale(1.1);
  }

  // When animation is complete it should return to initial position and size:
  100% {
    transform: rotate(360deg) scale(1);
  }
`

export const commonSpinnerStyles = css`
  animation: ${spinnerKeyframes} 1s ease-in-out infinite;
  background-color: ${({ theme }) => theme.colors.brandBlue.dark100};
  background: transparent;
  border: 2px solid ${({ theme }) => theme.colors.brandBlue.dark100}; // Must be before border-bottom-color and border-top-color
  border-bottom-color: transparent;
  border-radius: 50%;
  border-top-color: transparent;
  color: ${({ theme }) => theme.colors.brandBlue.dark100};
  content: '';
  display: inline-block;
`

// Until we get to StyledComponents v5 we have to use the backtick syntax in order to use keyframes
// that define custom animations:
const StyledSpinnerContents = styled.div`
  ${commonSpinnerStyles};
  height: 50px;
  left: 50%;
  margin-left: -25px; // Half the size of the spinner to center it.
  margin-top: -25px; // Half the size of the spinner to center it.
  position: absolute;
  top: 50%;
  width: 50px;
`

export interface SpinnerProps {
  className?: string
  loading?: boolean
  processing?: boolean
}

// This accepts both loading and processing so the semantically-appropriate prop can be
// used whether the user is waiting for data to load or waiting on the response to an
// action taken by the user.
const Spinner: React.VFC<SpinnerProps> = ({ className, loading = false, processing = false }) => {
  if (loading || processing) {
    return (
      <div className={className} data-cy="spinner">
        <StyledSpinnerContents />
      </div>
    )
  }

  return null
}

const StyledSpinner = styled(Spinner)({
  margin: '0 auto',
  paddingTop: '120px',
  position: 'relative',
})

export default StyledSpinner
